<template>
  <div class="licensing-settings">
    <div class="licensing-settings__header">
      <div class="licensing-settings__header__title">
        {{ $t('licensing.settings.title') }}
      </div>
      <span
        v-if="tenantTypeLabel"
        class="licensing-settings__type-badge"
        :class="`licensing-settings__type-badge--${tenantType}`"
      >
        {{ tenantTypeLabel }}
      </span>
    </div>

    <div v-if="loading" class="licensing-settings__loading">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="!licenseData || !licenseData.license" class="licensing-settings__empty">
      <v-icon large color="grey">info</v-icon>
      <span>{{ $t('licensing.settings.no.license') }}</span>
    </div>

    <div v-else>
      <!-- MSSP: tabs for License Management / Organizations -->
      <v-tabs
        v-if="isMssp"
        v-model="activeTab"
        class="licensing-settings__tabs"
        background-color="transparent"
        slider-color="var(--brand-primary)"
      >
        <v-tab>{{ $t('licensing.settings.tab.management') }}</v-tab>
        <v-tab>{{ $t('licensing.settings.tab.organizations') }}</v-tab>
      </v-tabs>

      <!-- Tab 1 / Non-MSSP: License Management grid -->
      <div v-show="!isMssp || activeTab === 0" class="licensing-settings__grid">
        <license-info-panel
          class="licensing-settings__grid-info"
          :license="license"
          :license-rows="licenseRows"
          :tenant-name="tenantName"
          :product-label="productLabel"
          :days-until-expiry="daysUntilExpiry"
          :is-expired="isExpired"
          :is-expiring-soon="isExpiringSoon"
          @activate="openActivateModal(null)"
        />

        <div v-if="!isMssp" class="licensing-settings__grid-right">
          <license-usage-panel
            :permissions="permissions"
            :asset-count="assetCount"
            :domain-count="domainCount"
            :keyword-count="keywordCount"
            :api-scan-count="apiScanCount"
          />

          <license-addons-panel :addon-list="addonList" />
        </div>

        <div v-else class="licensing-settings__grid-right">
          <org-summary-panel
            :organizations="organizations"
            @go-to-orgs="activeTab = 1"
          />
        </div>
      </div>

      <!-- Tab 2: Organizations (MSSP only) -->
      <div v-if="isMssp && activeTab === 1" class="licensing-settings__orgs">
        <div class="org-toolbar">
          <div class="org-toolbar__search">
            <v-icon small class="org-toolbar__search-icon">search</v-icon>
            <input
              v-model="orgSearch"
              type="text"
              class="org-toolbar__search-input"
              :placeholder="$t('licensing.settings.orgs.search')"
            >
          </div>
          <div class="org-toolbar__filters">
            <div
              v-for="(group, gi) in orgFilterGroups"
              :key="gi"
              class="filter-group"
            >
              <span class="filter-group__label">{{ filterGroupLabels[gi] }}</span>
              <div class="filter-group__chips">
                <button
                  v-for="f in group"
                  :key="f.value"
                  class="filter-chip"
                  :class="{ 'filter-chip--active': orgFilter === f.value }"
                  @click="setOrgFilter(f.value)"
                >
                  {{ f.label }}
                  <span v-if="f.count != null" class="filter-chip__count">{{ f.count }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="org-summary">
          {{ filteredOrganizations.length }} {{ filteredOrganizations.length === 1 ? 'organization' : 'organizations' }}
        </div>

        <div
          v-if="filteredOrganizations.length === 0"
          class="licensing-settings__empty"
        >
          <v-icon small color="grey">search_off</v-icon>
          <span>{{ $t('licensing.settings.orgs.no.results') }}</span>
        </div>

        <template v-else>
          <div class="org-cards">
            <org-license-card
              v-for="org in paginatedOrganizations"
              :key="org.id"
              :org="org"
              @activate="openActivateModal"
            />
          </div>

          <div
            v-if="orgTotalPages > 1"
            class="org-pagination"
          >
            <button
              class="org-pagination__btn"
              :disabled="orgPage <= 1"
              aria-label="Previous page"
              @click="orgPage--"
            >
              <v-icon small>chevron_left</v-icon>
            </button>
            <span class="org-pagination__info">
              {{ orgPage }} / {{ orgTotalPages }}
            </span>
            <button
              class="org-pagination__btn"
              :disabled="orgPage >= orgTotalPages"
              aria-label="Next page"
              @click="orgPage++"
            >
              <v-icon small>chevron_right</v-icon>
            </button>
          </div>
        </template>
      </div>

      <!-- License Activation Modal -->
      <license-activation-modal
        :visible="showActivateModal"
        :org-id="activateOrgId"
        :is-mssp="isMssp"
        @close="closeActivateModal"
        @activated="onLicenseActivated"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { LOAD_LICENSING } from '@/store/user/actionTypes';
import LicenseActivationModal from '@/components/Settings/LicenseActivationModal.vue';
import LicenseInfoPanel from '@/components/Settings/LicenseInfoPanel.vue';
import LicenseUsagePanel from '@/components/Settings/LicenseUsagePanel.vue';
import LicenseAddonsPanel from '@/components/Settings/LicenseAddonsPanel.vue';
import OrgSummaryPanel from '@/components/Settings/OrgSummaryPanel.vue';
import OrgLicenseCard from '@/components/Settings/OrgLicenseCard.vue';
import {
  ADDON_LABELS, ADDON_KEYS, MODULE_ADDON_KEYS, getOrgMaxUsage,
} from '@/components/Settings/licensing-constants';

export default {
  name: 'LicensingSettings',
  components: {
    LicenseActivationModal,
    LicenseInfoPanel,
    LicenseUsagePanel,
    LicenseAddonsPanel,
    OrgSummaryPanel,
    OrgLicenseCard,
  },
  data() {
    return {
      loading: false,
      activeTab: 0,
      orgSearch: '',
      debouncedOrgSearch: '',
      searchDebounceTimer: null,
      orgPage: 1,
      orgFilter: 'all',
      showActivateModal: false,
      activateOrgId: null,
      filterGroupLabels: ['Status', 'Usage', 'Expiration', 'Module'],
    };
  },
  async created() {
    this.loading = true;
    try {
      await this.$store.dispatch(LOAD_LICENSING);
    } catch (e) {
      // Licensing load failure is non-fatal — empty state will show
    } finally {
      this.loading = false;
    }
  },
  watch: {
    orgSearch(val) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedOrgSearch = val;
        this.orgPage = 1;
      }, 350);
    },
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  },
  computed: {
    ...mapGetters(['licenseData', 'companyData']),

    license() {
      return this.licenseData?.license || {};
    },
    permissions() {
      return this.licenseData?.permissions || {};
    },
    daysUntilExpiry() {
      return this.licenseData?.daysUntilExpiry ?? 0;
    },
    isExpired() {
      return this.permissions.isExpired === true;
    },
    isExpiringSoon() {
      return !this.isExpired && this.daysUntilExpiry <= 30;
    },
    isMssp() {
      // MSSP is carried by a dedicated CYN-MSSP config row, surfaced by the
      // backend as `isMssp` (and reflected in permissions.modules). Do not rely
      // on the base row's licenseType — resolveActiveLicense prefers the base
      // row, so that field is 'regular' for MSSP tenants.
      return this.licenseData?.isMssp === true ||
        (this.permissions.modules || []).includes('mssp');
    },
    tenantType() {
      if (this.isMssp) return 'mssp';
      return this.licenseData?.tenantType || null;
    },
    tenantTypeLabel() {
      const LABELS = {
        mssp: 'MSSP', pov_poc: 'PoV / PoC', pre_sale: 'Pre-Sale', regular: 'Regular',
      };
      return LABELS[this.tenantType] || null;
    },
    licenseRows() {
      const MODULE_ORDER = {
        platform_access: 0,
        mssp: 1,
        base: 2,
        infostealers: 3,
        tprm: 4,
        cloud_compliance: 5,
        threat_canvas: 6,
        white_labeling: 7,
        dashboard_date_filter: 8,
        api: 9,
      };
      const all = (this.licenseData?.allLicenses || []).map(row => ({
        ...row,
        displayName: row.skuDetails?.moduleLabel || row.skuDetails?.displayName || 'Unknown',
      }));
      // Sort: platform_access first, then by module order
      const sorted = all.slice().sort((a, b) => {
        const aOrder = MODULE_ORDER[a.skuDetails?.module] ?? 99;
        const bOrder = MODULE_ORDER[b.skuDetails?.module] ?? 99;
        return aOrder - bOrder;
      });
      // MSSP tenant-level: show platform access + mssp config
      if (this.isMssp) {
        return sorted.filter(
          row => row.skuDetails?.module === 'platform_access' ||
            row.skuDetails?.module === 'mssp',
        );
      }
      return sorted;
    },
    tenantName() {
      return this.companyData?.name || '—';
    },
    productLabel() {
      const rows = this.licenseData?.allLicenses || [];
      const baseLic = rows.find(r => r.skuDetails?.module === 'base' && !r.isExpired);
      return baseLic?.skuDetails?.displayName || 'CTEM';
    },
    addonList() {
      const enabledAddons = this.permissions.enabledAddons || [];
      const activeModules = this.permissions.modules || [];
      const enabled = new Set(enabledAddons);
      // Module-based addons: mark active when the module is licensed
      MODULE_ADDON_KEYS.forEach(mod => {
        if (activeModules.includes(mod)) enabled.add(mod);
      });
      return ADDON_KEYS.map(key => ({
        key,
        label: ADDON_LABELS[key],
        active: enabled.has(key),
      }));
    },
    orgFilterGroups() {
      const orgs = this.organizations;
      const licensed = orgs.filter(o => o.licenses && o.licenses.length > 0);
      const unlicensed = orgs.filter(o => !o.licenses || o.licenses.length === 0);

      // Usage-based counts
      const capped = orgs.filter(o => this.getOrgMaxUsage(o) >= 100);
      const highUsage = orgs.filter(o => {
        const u = this.getOrgMaxUsage(o);
        return u >= 75 && u < 100;
      });
      const expired = orgs.filter(o => (o.licenses || []).some(l => l.isExpired));
      const expiring = orgs.filter(o => (o.licenses || []).some(l => {
        if (l.isExpired || !l.expiresAt) return false;
        const days = Math.ceil((new Date(l.expiresAt).getTime() - Date.now()) / 86400000);
        return days <= 30;
      }));

      // Collect unique module labels from org licenses
      const moduleCounts = {};
      licensed.forEach(o => {
        const seen = new Set();
        (o.licenses || []).forEach(lic => {
          const mod = lic.skuDetails?.module;
          if (mod && !seen.has(mod)) {
            seen.add(mod);
            moduleCounts[mod] = (moduleCounts[mod] || 0) + 1;
          }
        });
      });

      const MODULE_LABELS = {
        base: 'CTEM',
        infostealers: 'Infostealers',
        tprm: 'TPRM',
        cloud_compliance: 'CSPM',
        threat_canvas: 'Threat Canvas',
        api: 'API',
      };

      // Group 1: Status
      const statusGroup = [
        { value: 'all', label: 'All', count: orgs.length },
        { value: 'licensed', label: 'Licensed', count: licensed.length },
        { value: 'unlicensed', label: 'Unlicensed', count: unlicensed.length },
      ];

      // Group 2: Usage
      const usageGroup = [
        { value: 'capped', label: 'Usage 100%', count: capped.length },
        { value: 'high-usage', label: 'Usage > 75%', count: highUsage.length },
      ];

      // Group 3: Expiration
      const expirationGroup = [
        { value: 'expiring', label: 'Expires < 30d', count: expiring.length },
        { value: 'expired', label: 'Expired', count: expired.length },
      ];

      // Group 4: Modules
      const moduleGroup = [];
      Object.keys(moduleCounts).forEach(mod => {
        if (MODULE_LABELS[mod]) {
          moduleGroup.push({
            value: `module:${mod}`,
            label: MODULE_LABELS[mod],
            count: moduleCounts[mod],
          });
        }
      });

      const groups = [statusGroup, usageGroup, expirationGroup];
      if (moduleGroup.length) groups.push(moduleGroup);
      return groups;
    },
    filteredOrganizations() {
      let list = this.organizations;

      // Apply filter chip
      if (this.orgFilter === 'licensed') {
        list = list.filter(o => o.licenses && o.licenses.length > 0);
      } else if (this.orgFilter === 'unlicensed') {
        list = list.filter(o => !o.licenses || o.licenses.length === 0);
      } else if (this.orgFilter === 'capped') {
        list = list.filter(o => this.getOrgMaxUsage(o) >= 100);
      } else if (this.orgFilter === 'high-usage') {
        list = list.filter(o => {
          const u = this.getOrgMaxUsage(o);
          return u >= 75 && u < 100;
        });
      } else if (this.orgFilter === 'expiring') {
        list = list.filter(o => (o.licenses || []).some(l => {
          if (l.isExpired || !l.expiresAt) return false;
          const days = Math.ceil((new Date(l.expiresAt).getTime() - Date.now()) / 86400000);
          return days <= 30;
        }));
      } else if (this.orgFilter === 'expired') {
        list = list.filter(o => (o.licenses || []).some(l => l.isExpired));
      } else if (this.orgFilter.startsWith('module:')) {
        const mod = this.orgFilter.replace('module:', '');
        list = list.filter(o => (o.licenses || []).some(lic => lic.skuDetails?.module === mod));
      }

      // Apply search
      const q = this.debouncedOrgSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(org => org.name.toLowerCase().includes(q));
      }

      return list;
    },
    orgTotalPages() {
      return Math.ceil(this.filteredOrganizations.length / 4) || 1;
    },
    paginatedOrganizations() {
      const start = (this.orgPage - 1) * 4;
      return this.filteredOrganizations.slice(start, start + 4);
    },
    organizations() {
      const raw = this.licenseData?.organizations || [];
      const usage = this.licenseData?.usage || {};
      const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6'];
      return raw.map((org, i) => {
        const activeLicenses = (org.licenses || []).filter(l => !l.isExpired);
        const licenseLabels = activeLicenses
          .map(l => l.skuDetails?.displayName)
          .filter(Boolean);
        const words = (org.name || '').split(/\s+/).filter(Boolean);
        const initials = words.length >= 2
          ? (words[0][0] + words[1][0]).toUpperCase()
          : (org.name || '?').slice(0, 2).toUpperCase();
        return {
          ...org,
          licenseLabels,
          initials,
          color: COLORS[i % COLORS.length],
          usage: org.usage || usage,
        };
      });
    },
    assetCount() {
      return this.licenseData?.usage?.assetCount ?? 0;
    },
    domainCount() {
      // For TPRM domain meter, use supply chain domain count; for general display use total
      return this.licenseData?.usage?.supplyChainDomainCount ?? this.licenseData?.usage?.domainCount ?? 0;
    },
    keywordCount() {
      return this.licenseData?.usage?.platformKeywordCount ?? 0;
    },
    apiScanCount() {
      return this.licenseData?.usage?.apiScanCount ?? 0;
    },
  },
  methods: {
    openActivateModal(orgId) {
      this.activateOrgId = orgId;
      this.showActivateModal = true;
    },
    setOrgFilter(value) {
      this.orgFilter = value;
      this.orgPage = 1;
    },
    getOrgMaxUsage,
    closeActivateModal() {
      this.showActivateModal = false;
      this.activateOrgId = null;
    },
    async onLicenseActivated() {
      this.closeActivateModal();
      this.loading = true;
      try {
        await this.$store.dispatch(LOAD_LICENSING);
      } catch (e) {
        // Reload failure is non-fatal
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.licensing-settings {
  height: 100%;
  margin-left: 3%;
  padding: 0 $space-20 $space-10 $space-8;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: $space-3;
    margin-bottom: 1%;
    margin-top: 2%;

    &__title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-xl;
      font-weight: bold;
      line-height: $line-height-tight;
      letter-spacing: 0;
      margin-bottom: $space-5;
    }
  }

  &__type-badge {
    font-family: $font-family-bold;
    font-size: $font-size-sm;
    font-weight: bold;
    padding: $space-1 $space-3;
    border-radius: $radius-full;
    border: $border-width-thin solid var(--border-primary);
    color: var(--text-secondary);
    margin-bottom: $space-5;

    &--mssp {
      border-color: var(--brand-primary);
      color: var(--brand-primary);
    }
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: $space-15 0;
  }

  &__empty {
    display: flex;
    align-items: center;
    gap: $space-3;
    padding: $space-10 0;
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
  }

  &__tabs {
    margin-bottom: $space-5;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: $space-6;
    align-items: start;
  }

  &__grid-info {
    // Stretch to match right column height
    align-self: stretch;
  }

  &__grid-right {
    display: flex;
    flex-direction: column;
    gap: $space-6;
  }

  &__orgs {
    padding-top: $space-2;
  }
}

// Org toolbar — search + filter chips
.org-toolbar {
  display: flex;
  flex-direction: column;
  gap: $space-3;
  margin-bottom: $space-4;

  &__search {
    display: flex;
    align-items: center;
    gap: $space-2;
    max-width: 320px;
    padding: $space-2 $space-3;
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-full;
    background: var(--bg-secondary);

    &:focus-within {
      border-color: var(--brand-primary);
    }
  }

  &__search-icon {
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  &__search-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    color: var(--text-primary);

    &::placeholder { color: var(--text-secondary); opacity: 0.6; }
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: $space-4;
    align-items: flex-start;
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: $space-1-5;

  &__label {
    font-family: $font-family-regular;
    font-size: $font-size-xs;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wider;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: $space-1-5;
  }
}

.org-summary {
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  color: var(--text-secondary);
  margin-bottom: $space-2;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: $space-1-5;
  padding: $space-1-5 $space-3;
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-full;
  background: var(--bg-secondary);
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
  transition: all $transition-duration-fast $transition-timing-ease;

  &:hover {
    border-color: var(--brand-primary);
  }

  &:focus-visible {
    outline: $border-width-medium solid var(--brand-primary);
    outline-offset: $border-width-thin;
  }

  &--active {
    background: var(--brand-primary);
    border-color: var(--brand-primary);
    color: $white;

    .filter-chip__count {
      background: rgba(255, 255, 255, 0.25);
      color: $white;
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 $space-1-5;
    border-radius: $radius-full;
    background: var(--bg-tertiary, $gray-100);
    font-family: $font-family-bold;
    font-size: $font-size-xs;
    color: var(--text-secondary);
    line-height: 1;
  }
}

// Org cards (MSSP tab 2) — grid ensures equal row heights
.org-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $space-4;
}

.org-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $space-3;
  margin-top: $space-5;

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-6;
    background: var(--bg-secondary);
    cursor: pointer;
    color: var(--text-primary);

    &:hover:not(:disabled) { background: var(--bg-tertiary, $gray-100); }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: $border-width-medium solid var(--brand-primary);
      outline-offset: -#{$border-width-medium};
    }
  }

  &__info {
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    color: var(--text-secondary);
  }
}
</style>
